<template>
  <ModalMask @click="onClose">
    <div
      class="modal"
      :class="[
        size,
        {
          dialog: hideCloseButton,
          noScroll,
          'page-feed': pageFeed,
          'static-footer': staticFooter,
        },
      ]"
      :style="{ width: `${modelSize}px` }"
    >
      <header
        class="header"
        :class="{ 'hide-border': hideCloseButton }"
        :style="`grid-template-columns: 1fr max-content 1fr ${$slots['header-right'] ? '1fr' : ''};`"
      >
        <slot name="header">
          <div class="left"><slot name="header-left" /></div>
          <slot name="title">
            <h1>{{ title }}</h1>
          </slot>
          <div v-if="$slots['header-right']" class="right"><slot name="header-right" /></div>
          <Button v-if="!hideCloseButton" icon class="close" @click="onClose"><CloseIcon /></Button>
        </slot>
      </header>
      <main class="modal-main">
        <slot name="content">
          <div class="content" :style="`grid-gap: ${gap}px`">
            <slot />
          </div>
        </slot>
      </main>
      <footer v-if="hasFooter && footer" class="footer" :class="{ 'hide-border': hideCloseButton }">
        <div class="left"><slot name="footer-left" /></div>
        <div class="center"><slot name="footer-center" /></div>
        <div class="right"><slot name="footer-right" /></div>
      </footer>
      <LoadingIndicator v-if="loading" />
    </div>
  </ModalMask>
</template>

<script>
import ModalMask from '@/components/molecules/ModalMask'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import Button from '@/components/atoms/Button'
import CloseIcon from '@/components/icons/close'

export default {
  name: 'Modal',
  components: {
    ModalMask,
    Button,
    LoadingIndicator,
    CloseIcon,
  },
  props: {
    /** タイトル */
    title: String,
    /** サイズ */
    size: {
      type: [String, Array],
      default: undefined,
      validator(value) {
        return (Array.isArray(value) ? value : value.split(' ')).every(val =>
          ['big', 'middle', 'max-height', ''].includes(val),
        )
      },
    },
    /** modelのサイズを細かく指定 */
    modelSize: { type: Number, default: null },
    /** ローディング中 */
    loading: Boolean,
    /** コンテンツエリアの grid-gap */
    gap: { type: Number, default: 16 },
    /** 閉じるボタン非表示  TODO molecules/ModalDialogに移行したい*/
    hideCloseButton: Boolean,
    /** ページ送り時のレイアウト*/
    pageFeed: Boolean,
    /** footerの表示 */
    footer: { type: Boolean, default: true },
    /** フッターを固定しない */
    staticFooter: Boolean,
    /** スクロール無し */
    noScroll: Boolean,
  },
  computed: {
    hasFooter() {
      return !!(this.$slots['footer-left'] || this.$slots['footer-center'] || this.$slots['footer-right'])
    },
  },
  methods: {
    onClose() {
      this.$emit('close')
    },
  },
}
</script>

<style lang="postcss" scoped>
.modal {
  background: var(--white);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  box-sizing: border-box;
  display: grid;
  grid-template-rows: max-content 1fr max-content;
  max-height: calc(100vh - 64px);
  transition: all 0.3s ease;
  width: 480px;

  > main {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  &.static-footer {
    overflow-y: auto;

    > main {
      overflow: initial;
    }
  }

  &.middle {
    height: 512px;
  }

  &.big {
    width: 624px;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      &:not(.dialog) {
        height: 100%;
        max-height: 100%;
      }
    }
    @media screen and (--sp-dialog) {
      &.dialog {
        width: calc(100% - 32px);
      }
    }
  }
}

.header {
  align-items: center;
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  display: grid;
  min-height: 56px;
  padding: 0 16px;
  word-break: keep-all;

  .static-footer & {
    background-color: var(--white);
    position: sticky;
    top: 0;
    z-index: 1000;
  }

  .noScroll & {
    border: 0;
  }

  &.hide-border {
    border: 0;
    padding-bottom: 0;
    padding-top: 32px;
  }

  h1 {
    font-size: 21px;
    text-align: center;
    white-space: pre-wrap;

    .responsive & {
      @media screen and (--sp-modal) {
        font-size: 16px;
      }
    }
  }

  .big & {
    word-break: break-all;

    h1 {
      line-height: 24px;
      max-width: 544px;
      padding: 16px 0;
    }
  }
}

.close {
  fill: var(--very-dark-gray-2);
  justify-self: flex-end;
  width: fit-content;
}

.modal-main {
  .noScroll & {
    overflow: hidden;
  }
}

.content {
  display: grid;
  padding: 32px;

  .middle & {
    padding: 24px;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      padding: 24px 16px;
    }
  }

  .responsive .page-feed & {
    @media screen and (--sp-modal) {
      padding: 0 16px;
    }
  }
}

.footer {
  align-items: center;
  border-top: 1px solid var(--border-color);
  box-sizing: border-box;
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  padding: 16px;

  .full & {
    border: 0;
    padding: 16px;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      .center {
        grid-auto-columns: 1fr;
        grid-gap: 16px;
      }

      >>> button {
        border-radius: 4px;
        font-size: 15px;
        height: 40px;
        line-height: normal;
        width: 100%;
      }
    }
  }

  .responsive .page-feed & {
    @media screen and (--sp-modal) {
      background: var(--mostly-white);
      border-top: none;
      box-shadow: 0 0 6px var(--very-light-gray);
      grid-template-columns: 1fr max-content max-content;

      .left {
        margin-right: 16px;
      }

      .right {
        grid-gap: 8px;
      }
    }
  }

  &.hide-border {
    border: 0;
    padding-bottom: 32px;
    padding-top: 0;
  }

  .center {
    align-items: center;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 32px;
    justify-content: center;
  }

  .right {
    align-items: center;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 16px;
    justify-content: flex-end;
  }
}

.full {
  background-color: rgba(0, 0, 0, 0);
  grid-gap: 0;
  min-height: 100vh;
  padding: 0;
  width: 100vw;

  .header {
    border: 0;
    height: 72px;
    padding: 8px;

    >>> svg {
      fill: var(--white);
      height: 40px;
      margin: 8px;
      width: 40px;
    }
  }
}

.max-height {
  height: calc(100vh - 48px);
}
</style>
